import axios from "axios";

const sh = axios.create({
  baseURL: "/",
  timeout: 10000,
  withCredentials: true,
  xsrfCookieName: "CSRF-TOKEN",
  xsrfHeaderName: "X-CSRF-TOKEN",
  headers: {
    Accept: "application/json",
  },
});

const cancellationTokens = {};

const cancelKeyFor = (config) => config.cancelKey || `${(config.method || "get").toLowerCase()}:${config.url}`;

// Rails verifies X-CSRF-TOKEN, but the CSRF-TOKEN cookie that axios reads is only set by
// Internal::BaseController. Everywhere else callers pass `authenticity_token` in the body, so a
// request that forgets it fails CSRF. Read the token from the layout's csrf_meta_tags instead,
// at request time (the DOM is parsed by then) and only when nothing already set the header.
const csrfMetaToken = () =>
  document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

sh.interceptors.request.use(
  (config) => {
    const cancelKey = cancelKeyFor(config);

    if (!config.headers["X-CSRF-TOKEN"]) {
      const token = csrfMetaToken();
      if (token) config.headers["X-CSRF-TOKEN"] = token;
    }

    // If a previous request for this cancel key is still active, cancel it
    if (cancellationTokens[cancelKey]) {
      cancellationTokens[cancelKey]("Request canceled due to new request");
    }

    // Create a new cancel token for this cancel key
    config.cancelToken = new axios.CancelToken(function executor(c) {
      cancellationTokens[cancelKey] = c;
    });
    config.__cancelKey = cancelKey;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

sh.interceptors.response.use(
  (response) => {
    const cancelKey = response.config?.__cancelKey || cancelKeyFor(response.config || {});
    if (cancelKey && cancellationTokens[cancelKey]) {
      delete cancellationTokens[cancelKey];
    }
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      // Important: return a rejected promise so callers' `.catch`/`.finally` run.
      // Returning a never-settling promise breaks loader state in callers.
      return Promise.reject(error);
    }
    const cancelKey = error.config?.__cancelKey || cancelKeyFor(error.config || {});
    if (cancelKey && cancellationTokens[cancelKey]) {
      delete cancellationTokens[cancelKey];
    }
    if (
      error.response?.status === 401 &&
      error.response?.headers?.["x-session-kicked"] === "true"
    ) {
      localStorage.setItem("sessionKicked", "true");
      const locale = window.location.pathname.split("/")[1] || "en";
      window.location.href = `/${locale}/recruiters/sign_in`;
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

export default sh;
